// src/AppRoutes.js
import './App.css';

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';

import { ScrollToTop } from './Hooks/usePreventScrolling';
import backend from './api';
import { connectSocket, getSocket } from './api/socket';

import { setUserData } from './redux-store/auth/actions';
import { authActions } from './redux-store';

// User Dashboard pages
import UserDashboardMainLayout from './Panels/UserDashboard/MainLayout/MainLayout';
import UserDashboardDashboard from './Panels/UserDashboard/Pages/Activity/Dashboard/Dashboard';
import UserDashboardUpgrades from './Panels/UserDashboard/Pages/MoreFeatures/Upgrades/Upgrades';
import UserDashboardSettings from './Panels/UserDashboard/Pages/Setup/Settings/Settings';

// Main website
import Home from './Panels/MainWebsite/Home';
import Signup from './MainWebsite/Pages/Auth/Signup';
import Auth from './MainWebsite/Pages/Auth/Auth';
import ContactUs from './MainWebsite/Pages/ContactUs/ContactUs';

// Profile Suite
import Profile from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/Profile';
import ItineraryList from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/ItineraryList';
import EmergencyContacts from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/EmergencyContacts';
import ItineraryForm from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/ItineraryForm';
import ChangeLanguage from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/ChangeLanguage';
import CustomerSupport from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/CustomerSupport';
import SpecialNeeds from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/SpecialNeeds';
import ContactsScreen from './Panels/UserDashboard/Pages/Setup/Settings/Components/Profile/ContactsScreen';

// Chatbot
import Chatbot from './Panels/UserDashboard/Pages/Activity/Chatbot/Chatbot';

// Guides
import GuidesList from './Panels/UserDashboard/Pages/Activity/Guides/GuidesList';
import GuideDetail from './Panels/UserDashboard/Pages/Activity/Guides/GuidesDetail';

// Community
import CommunityExplore from './Panels/UserDashboard/Pages/Activity/Community/CommunityExplore';
import CommunityDetails from './Panels/UserDashboard/Pages/Activity/Community/CommunityDetails';

// Groups
import GroupsList from './Panels/UserDashboard/Pages/Activity/groups/GroupsList';
import GroupChat from './Panels/UserDashboard/Pages/Activity/groups/GroupChat';
import CreateGroup from './Panels/UserDashboard/Pages/Activity/groups/CreateGroup';
import EditGroup from './Panels/UserDashboard/Pages/Activity/groups/EditGroup';
import GroupMap from './Panels/UserDashboard/Pages/Activity/Map/GroupMap';
import DmChat from './Panels/UserDashboard/Pages/Activity/groups/DmChat';

// Call
import CallPage from './Panels/UserDashboard/Pages/Activity/Call/CallPage';

// Map
import UserMap from './Panels/UserDashboard/Pages/Activity/Map/UserMap';
import ContentManager from './Panels/AdminPanel/ContentManager/ContentManager';

import LandingPage from './MainWebsite/Pages/LandingPage/LandingPage';

import PrivacyPolicy from './MainWebsite/Pages/PrivacyPolicy/PrivacyPolicy';
import Terms from './MainWebsite/Pages/Terms/Terms';
import ComplianceSecurity from './MainWebsite/Pages/ComplianceSecurity/ComplianceSecurity';

/* ------------------------ AgentSocketBridge (global) ----------------------- */

const PRIVATE_PREFIXES = [
  "/", "/profile", "/settings", "/groups", "/dm", "/call", "/map",
  "/community-explore", "/community-details", "/guides", "/chatbot", "/upgrade"
];
const PUBLIC_ONLY = ["/login", "/signup", "/contact-us"];

function isPrivatePath(pathname) {
  return PRIVATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function AgentSocketBridge() {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth?.user);

  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;

    const onAction = (payload) => {
      const a = payload || {};

      // Map agent dispatches -> Redux + localStorage
      if (a.type === "dispatch" && a.action) {
        if (a.action === "auth/setUserData" && a.payload) {
          // Persist exactly like your login() thunk does
          dispatch(setUserData(a.payload));
          try {
            localStorage.setItem('user', JSON.stringify({
              _id: a.payload._id,
              email: a.payload.email,
              fullname: a.payload.fullname || a.payload.name || "",
              mobileNumber: a.payload.mobileNumber || a.payload.phone || "",
              role: a.payload.role,
              status: a.payload.status,
              createdAt: a.payload.createdAt,
              updatedAt: a.payload.updatedAt,
              token: a.payload.token,
              profileExists: a.payload.profileExists,
            }));
          } catch {}
          return;
        }
        if (a.action === "auth/logout") {
          // Mirror your logout action behavior
          authActions?.logout && dispatch(authActions.logout());
          try { localStorage.removeItem("user"); } catch {}
          return;
        }
        // Re-broadcast for page-level listeners
        window.dispatchEvent(new CustomEvent("agent-dispatch", { detail: a }));
        return;
      }

      // Route-aware navigation
      if (a.type === "navigate" && a.path) {
        const dest = a.path;
        const loggedIn = !!user?._id;
        const wantsPrivate = isPrivatePath(dest);

        if (!loggedIn && wantsPrivate) {
          nav("/login", { state: { redirect: dest } });
          return;
        }
        if (loggedIn && (PUBLIC_ONLY.includes(dest) || dest === "/login")) {
          nav("/");
          return;
        }
        nav(dest);
        return;
      }

      // UI helpers
      if (a.type === "openModal" && a.id) {
        window.dispatchEvent(new CustomEvent("open-modal", { detail: a.id }));
      }
    };

    socket.on("agent:action", onAction);
    return () => socket.off("agent:action", onAction);
  }, [nav, dispatch, user?._id]);

  return null;
}

/* --------------------------------- Routes --------------------------------- */

const UserDashboardWithSideBar = (Component) => (
  <UserDashboardMainLayout component={<Component />} />
);

function ProfileRoutes() {
  return (
    <Routes>
      <Route path="language" element={UserDashboardWithSideBar(ChangeLanguage)} />
      <Route path="special-needs" element={UserDashboardWithSideBar(SpecialNeeds)} />
      <Route path="support" element={UserDashboardWithSideBar(CustomerSupport)} />
      <Route path="itineraries" element={UserDashboardWithSideBar(ItineraryList)} />
      <Route path="itineraries/add" element={UserDashboardWithSideBar(ItineraryForm)} />
      <Route path="itineraries/:id" element={UserDashboardWithSideBar(ItineraryForm)} />
      <Route path="contacts" element={UserDashboardWithSideBar(ContactsScreen)} />
      <Route path="emergency-contact/add" element={UserDashboardWithSideBar(EmergencyContacts)} />
      <Route path="emergency-contact/:id" element={UserDashboardWithSideBar(EmergencyContacts)} />
    </Routes>
  );
}

const UserDashboardLoggedInRoutes = () => (
  <Routes>
    <Route path="/" element={UserDashboardWithSideBar(UserDashboardDashboard)} />
    <Route path="/settings/profile" element={UserDashboardWithSideBar(UserDashboardSettings)} />
    <Route path="/settings" element={UserDashboardWithSideBar(UserDashboardSettings)} />
    <Route path="/profile" element={UserDashboardWithSideBar(Profile)} />
    <Route path="/settings/profile/*" element={<ProfileRoutes />} />

    <Route path="/upgrade" element={UserDashboardWithSideBar(UserDashboardUpgrades)} />
    <Route path="/chatbot" element={UserDashboardWithSideBar(Chatbot)} />

    <Route path="/guides" element={UserDashboardWithSideBar(GuidesList)} />
    <Route path="/guides/:slug" element={UserDashboardWithSideBar(GuideDetail)} />

    <Route path="/community-explore" element={UserDashboardWithSideBar(CommunityExplore)} />
    <Route path="/community-details/:id" element={UserDashboardWithSideBar(CommunityDetails)} />

    <Route path="/groups" element={UserDashboardWithSideBar(GroupsList)} />
    <Route path="/groups/:groupId/chat" element={UserDashboardWithSideBar(GroupChat)} />
    <Route path="/groups/create" element={UserDashboardWithSideBar(CreateGroup)} />
    <Route path="/groups/:groupId/edit" element={UserDashboardWithSideBar(EditGroup)} />
    <Route path="/groups/:groupId/map" element={UserDashboardWithSideBar(GroupMap)} />
    <Route path="/dm/:peerId" element={UserDashboardWithSideBar(DmChat)} />
    <Route path="/call/:peerId" element={UserDashboardWithSideBar(CallPage)} />
    <Route path="/map/:targetUserId" element={UserDashboardWithSideBar(UserMap)} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<Terms />} />
    <Route path="/compliance-and-security" element={<ComplianceSecurity />} />
    <Route path="*" element={UserDashboardWithSideBar(UserDashboardDashboard)} />
  </Routes>
);

const AdminDashboardLoggedInRoutes = () => (
  <Routes>
    <Route path="/" element={UserDashboardWithSideBar(ContentManager)} />
    <Route path="/guides" element={UserDashboardWithSideBar(GuidesList)} />
    <Route path="/guides/:slug" element={UserDashboardWithSideBar(GuideDetail)} />
    <Route path="*" element={UserDashboardWithSideBar(ContentManager)} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<Terms />} />
    <Route path="/compliance-and-security" element={<ComplianceSecurity />} />
  </Routes>
);
const MainWebsiteLoggedOutRoutes = () => (
  <Routes>
    <Route path="/signup" element={<Signup />} />
    <Route path="/login" element={<Auth />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<Terms />} />
    <Route path="/compliance-and-security" element={<ComplianceSecurity />} />

    <Route path="/contact-us" element={<ContactUs />} />
     <Route path="/chatbot" element={<Chatbot/>} />

    <Route path="/" element={<LandingPage />} />
        <Route path="/old" element={<Home />} />

    <Route path="*" element={<Auth />} />
  </Routes>
);

function AppRoutes() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // useEffect(() => {
  //   if (!user?._id) return;

  //   const userId = String(user._id);
  //   connectSocket(userId);

  //   const fetchUser = async () => {
  //     const res = await backend.put(`/api/user/${userId}`);
  //     const updated = { ...user, ...res.data.data };
  //     await dispatch(setUserData(updated));
  //   };
  //   fetchUser();
  // }, [user?._id, dispatch]);

  return (
    <BrowserRouter>
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <ScrollToTop />
        <AgentSocketBridge />
        {user && user.role=='admin'?<AdminDashboardLoggedInRoutes/> :user && user.role!='admin'?<UserDashboardLoggedInRoutes /> : <MainWebsiteLoggedOutRoutes />}
      </SnackbarProvider>
    </BrowserRouter>
  );
}

export default AppRoutes;
